import AutoLayoutSelector from "./AutoLayoutSelector";
import ColorInput from "./ColorInput";
import EmptyInputState from "./EmptyInputState";

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/components/ui/hover-card";
import { CaretDownIcon, ResetIcon } from "@radix-ui/react-icons";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "../../../../ui/command";
import { NewPropertyState } from "@/components/RightStyle/usePropertyStateV2";
import { Loader2 } from "lucide-react";

interface ClickableIconProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

const ClickableIcon = ({ children, ...props }: ClickableIconProps) => {
  return (
    <div
      className="w-4 h-4 flex justify-center items-center p-1 hover:bg-secondary-400 hover:text-secondary-900 text-white cursor-pointer"
      {...props}
    >
      {children}
    </div>
  );
};

interface ComboboxContextType {
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
}

const ComboboxContext = createContext<ComboboxContextType | undefined>(
  undefined
);

const useComboboxContainer = () => {
  const context = useContext(ComboboxContext);

  if (context === undefined) {
    throw new Error(
      "useComboboxContainer must be used within a ComboboxContainer"
    );
  }

  return context;
};

interface ComboboxContainerProps {
  className?: string;
  children: React.ReactNode;
}

const ComboboxContainer: React.FC<ComboboxContainerProps> = ({
  children,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={containerRef} className={className} style={{ width: "100%" }}>
      <ComboboxContext.Provider
        value={{
          containerRef: containerRef,
        }}
      >
        {children}
      </ComboboxContext.Provider>
    </div>
  );
};

interface ComboBoxOptionType {
  tailwind: string;
  css: string;
}

interface ComboBoxProps {
  options: ComboBoxOptionType[];
  onOptionSelected: (
    option: ComboBoxOptionType,
    event?: React.MouseEvent
  ) => void;
  input: string;
  open: boolean;
  closeCombobox: () => void;
  buildArbitraryClass?: (input: string) => ComboBoxOptionType;
  buildOptionPreview?: (option: ComboBoxOptionType) => ReactNode;
  anchorRef: React.RefObject<HTMLElement>;
  onMouseDown?: React.MouseEventHandler<HTMLDivElement>;
  onMouseUp?: React.MouseEventHandler<HTMLDivElement>;
}

const ComboBox: React.FC<ComboBoxProps> = ({
  options,
  onOptionSelected,
  input,
  open,
  closeCombobox,
  buildArbitraryClass,
  buildOptionPreview,
  anchorRef,
  onMouseDown,
  onMouseUp,
}) => {
  const { containerRef } = useComboboxContainer();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);

  const [firstNonNullInput, setFirstNonNullInput] = useState<string | null>(
    null
  );
  const [inputChanged, setInputChanged] = useState(false);
  const activeOptionRef = useRef<HTMLDivElement>(null);
  const comboboxRef = useRef(null);

  const [comboboxStyle, setComboboxStyle] = useState(null);

  const BATCH_SIZE = 10;
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = document.getElementById("styling-panel-portal-root");
    setPortalRoot(root);
  }, []);

  useEffect(() => {
    const root = document.getElementById("styling-panel-portal-root");
    setPortalRoot(root);
  }, []);

  useEffect(() => {
    if (open && anchorRef.current && containerRef.current) {
      const inputRect = anchorRef.current.getBoundingClientRect();

      const containerRect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const spaceAbove = inputRect.top;
      const spaceBelow = viewportHeight - inputRect.bottom;

      const useBottomPosition = spaceBelow > spaceAbove;

      const position = useBottomPosition
        ? { top: `${inputRect.bottom}px` }
        : { bottom: `${viewportHeight - inputRect.top}px` };

      const width = containerRect.width;

      setComboboxStyle({
        ...position,
        // @ts-expect-error TS2345
        left: `${containerRect.left}px`,
        width: `${width}px`,
      });
    }
  }, [open, anchorRef, containerRef]);

  /**
   * Tracks the first non-null input (ie. the initial value.)
   *
   * Tracks changes to input and sets input changes.
   */
  useEffect(() => {
    if (input !== null && firstNonNullInput === null) {
      setFirstNonNullInput(input); // Store the first non-null input
    }
    if (firstNonNullInput !== null && input !== firstNonNullInput) {
      setInputChanged(true); // Mark as changed if different from first non-null input
    }
  }, [input]);

  /**
   * If there was a non-null input, and it's now empty, reset the active index.
   */
  useEffect(() => {
    if (input === "" && firstNonNullInput !== null) {
      setActiveIndex(null);
    }
  }, [input, firstNonNullInput]);

  const filteredOptions = useMemo(() => {
    let newOptions = options
      .map((option) => {
        let score = 0;

        if (!input) {
          score = 1; // So that all items are considered when there's no searchTerm
        } else {
          const term = input.toLowerCase();

          if (option.tailwind.toLowerCase() === term) {
            score += 10;
          } else if (option.tailwind.toLowerCase().startsWith(term)) {
            score += 5;
          } else if (option.tailwind.toLowerCase().includes(term)) {
            score += 3;
          }

          if (option.css.toLowerCase().includes(term)) {
            score += 1;
          }
        }

        return { ...option, score };
      })
      .sort((a, b) => b.score - a.score) // Sort items with higher scores first
      .filter((item) => item.score > 0); // Filter out non-matching items

    if (buildArbitraryClass && input.length) {
      newOptions.unshift({
        ...buildArbitraryClass(input),
        score: Number.MAX_SAFE_INTEGER,
      });
    }

    return newOptions;
  }, [
    input,
    open,
    options,
    buildArbitraryClass,
    inputChanged,
    firstNonNullInput,
  ]);

  const showAllOptions = firstNonNullInput !== null && !inputChanged;
  const optionsToShow = showAllOptions ? options : filteredOptions;
  const defaultIndex =
    filteredOptions.length > 1
      ? options.findIndex(
          (value: ComboBoxOptionType) =>
            value.tailwind === filteredOptions[1].tailwind
        )
      : -1;

  const indexOfActiveOption =
    filteredOptions.length > 1
      ? optionsToShow.findIndex(
          (value: ComboBoxOptionType) =>
            value.tailwind === filteredOptions[1].tailwind
        )
      : 0;

  const optionThatIsActiveRef = useRef<ComboBoxOptionType | null>(null);
  useEffect(() => {
    optionThatIsActiveRef.current =
      activeIndex !== null ? optionsToShow[activeIndex] : null;
  }, [activeIndex, optionsToShow]);

  /**
   * Reset first non-null input and input change state on close.
   */
  useEffect(() => {
    if (open && showAllOptions && activeIndex === null && defaultIndex >= 0) {
      setActiveIndex(defaultIndex);
    }

    if (!open) {
      setFirstNonNullInput(null);
      setInputChanged(false);
    }
  }, [open, showAllOptions, defaultIndex]);

  useEffect(() => {
    if (input !== null && input !== "" && firstNonNullInput !== null) {
      setActiveIndex(indexOfActiveOption);
    }
  }, [input]);

  /**
   * Click-away effect
   */
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        comboboxRef.current &&
        // @ts-expect-error TS2339
        !comboboxRef.current.contains(event.target as Node)
      ) {
        // Only close the combobox, do NOT auto-select the active option
        closeCombobox();
        setActiveIndex(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [comboboxRef]);

  /**
   * Arrow and enter selection for combobox
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        // Move focus down
        setActiveIndex((prevIndex) =>
          prevIndex === null || prevIndex >= optionsToShow.length - 1
            ? 0
            : prevIndex + 1
        );
        event.preventDefault();
      } else if (event.key === "ArrowUp") {
        // Move focus up
        setActiveIndex((prevIndex) =>
          prevIndex === null || prevIndex <= 0
            ? optionsToShow.length - 1
            : prevIndex - 1
        );
        event.preventDefault();
      } else if (event.key === "Enter" && activeIndex !== null) {
        // Select the active item
        onOptionSelected(optionsToShow[activeIndex]);
        closeCombobox();
        event.preventDefault();
      } else if (event.key === "Escape") {
        closeCombobox();
        setActiveIndex(null);
        event.preventDefault();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeIndex, optionsToShow, onOptionSelected]);

  /**
   * Scroll list to match activeIndex
   */
  useEffect(() => {
    if (activeOptionRef.current) {
      const activeElement = activeOptionRef.current;
      activeElement.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      setVisibleCount((prev) =>
        Math.min(prev + BATCH_SIZE, optionsToShow.length)
      );
    }
  };

  useEffect(() => {
    setVisibleCount(BATCH_SIZE);
  }, [options, open, input]);

  if (filteredOptions.length === 0) {
    return null;
  }

  if (!comboboxStyle) {
    return null;
  }

  const comboboxContent = (
    <div
      className="fixed flex-grow z-[100002] text-xs"
      ref={comboboxRef}
      style={comboboxStyle}
      data-inside-modal="true"
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
    >
      <div className="p-2">
        {open && (
          <div
            className="bg-popover shadow-md rounder-sm border max-h-72 overflow-y-auto flex flex-col hide-scrollbar"
            ref={listRef}
            onScroll={handleScroll}
          >
            {optionsToShow.slice(0, visibleCount).map((cls, index) => (
              <div
                key={index}
                className={`p-2 flex-grow flex flex-row justify-between text-2xs space-x-1 items-center cursor-pointer ${
                  index === activeIndex ? "bg-accent" : "hover:bg-accent"
                }`}
                onClick={(e) => onOptionSelected(cls)}
                ref={index === activeIndex ? activeOptionRef : null}
              >
                <div className="flex flex-row space-x-2 items-center">
                  {buildOptionPreview && (
                    <div className="w-4 h-4">{buildOptionPreview(cls)}</div>
                  )}
                  <div className="font-medium flex-grow whitespace-nowrap line-clamp-1">
                    {cls.tailwind}
                  </div>
                </div>
                <div className="flex flex-col flex-shrink">
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      <div className="flex flex-col flex-shrink text-ellipsis overflow-hidden">
                        {cls.css
                          .split("\n")
                          .map((cssLine: string, index: number) => {
                            return (
                              <div
                                key={index}
                                className="font-light text-right flex-shrink text-secondary-n-500 line-clamp-1"
                              >
                                {cssLine}
                              </div>
                            );
                          })}
                      </div>
                    </HoverCardTrigger>
                    <HoverCardContent
                      className="flex-grow p-0"
                      side="bottom"
                      avoidCollisions={false}
                    >
                      <div className="space-y-1 flex flex-col text-xs w-full overflow-hidden pt-4">
                        <h4 className="font-semibold px-4">{cls.tailwind}</h4>
                        <pre className="w-full overflow-auto px-4 pb-4">
                          <code className="css">{cls.css}</code>
                        </pre>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </div>
              </div>
            ))}

            {visibleCount < optionsToShow.length && (
              <div className="flex justify-center py-2">
                <Loader2 className="h-5 w-5 text-secondary-n-500 animate-spin" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  if (!portalRoot) {
    return comboboxContent;
  }

  return ReactDOM.createPortal(comboboxContent, portalRoot);
};

interface DropdownOptionType {
  label: string;
  value: any;
  icon: ReactNode;
}

interface DropdownInputProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  dropdownOptions: DropdownOptionType[];
  onDropdownSelected: (
    option: DropdownOptionType,
    event?: React.MouseEvent
  ) => void;
  className?: string;
}

const DropdownInput: React.FC<DropdownInputProps> = ({
  visible,
  setVisible,
  dropdownOptions,
  onDropdownSelected,
  className,
}) => {
  const dropdownRef = useRef(null);
  /**
   * Click-away effect
   */
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        // @ts-expect-error TS2339
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setVisible(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [dropdownRef]);

  return (
    visible &&
    dropdownOptions && (
      <div
        className={
          "absolute top-full right-0 mt-2 w-full border border-secondary-500 text-xs text-white z-10 rounded-md " +
          (className ? className : "")
        }
        ref={dropdownRef}
        data-inside-modal="true"
      >
        <Command className="rounded-xl">
          <CommandList className="rounded-xl">
            <CommandGroup>
              {dropdownOptions.map((option, index) => {
                return (
                  <CommandItem
                    key={index}
                    className="relative text-2xs flex flex-row gap-2"
                    onSelect={() => {
                      onDropdownSelected(option);
                    }}
                  >
                    <span>{!!option.icon && option.icon}</span>
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </div>
    )
  );
};

interface TooltipProps {
  tooltipContent?: string | null;
}

interface DiscreteOption extends TooltipProps {
  key: string;
  option: ComboBoxOptionType;
  labelContent: ReactNode;
}

interface DiscreteInputProps {
  options: DiscreteOption[];
  set: boolean;
  onOptionSelected: (option: DiscreteOption) => void;
  selected: string;
  disabled?: boolean;
  onDeselect?: (option: DiscreteOption) => void;
  className?: string;
}

const DiscreteInput: React.FC<DiscreteInputProps> = ({
  options,
  set = false,
  onOptionSelected,
  selected,
  disabled = false,
  onDeselect,
  className,
}) => {
  const anySelected = useMemo(() => {
    return options.find((option) => option.key === selected);
  }, [options, selected]);

  return (
    <div
      className={cn(
        "flex flex-row items-center p-[2px] gap-0.5 relative border border-transparent bg-secondary-n-950 rounded-md",
        className
      )}
    >
      {options.map((option, index) => {
        const isSelected = selected === option.key;
        return (
          <div className="h-7 flex grow" key={index}>
            <div
              key={option.key}
              onClick={() => {
                if (disabled) {
                  return;
                }

                if (isSelected && onDeselect) {
                  onDeselect(option);
                  return;
                }

                onOptionSelected(option);
              }}
              className={cn(
                `cursor-pointer p-1 flex flex-grow shrink items-center rounded-md justify-center text-secondary-n-700 hover:text-icons-primary hover:bg-secondary-n-900`,
                isSelected && "bg-secondary-n-900 text-icons-primary",
                !anySelected &&
                  "text-secondary-n-700 hover:text-secondary-n-50",
                index === 0 && "rounded-l-md",
                index === options.length - 1 && "rounded-r-md"
              )}
              data-tooltip-id="tooltip"
              data-tooltip-content={option.tooltipContent}
              data-tooltip-place="bottom"
              data-tooltip-offset={5}
            >
              {option.labelContent}
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface ToggleInputProps extends TooltipProps {
  toggled: boolean;
  labelContent: ReactNode;
  onToggle: (toggled: boolean) => void;
  set?: boolean;
  disabled?: boolean;
}

const ToggleInput: React.FC<ToggleInputProps> = ({
  toggled,
  labelContent,
  onToggle,
  set,
  disabled,
  tooltipContent,
}) => {
  return (
    <div className="h-8 flex grow">
      <div
        onClick={() => {
          !disabled && onToggle(!toggled);
        }}
        className={`flex flex-row w-full relative border border-transparent hover:border-secondary-600 cursor-pointer p-1 items-center justify-center bg-secondary-200 rounded-md ${
          toggled ? "bg-secondary-500" : "text-secondary-600 hover:text-white"
        }`}
        data-tooltip-id="tooltip"
        data-tooltip-content={tooltipContent}
        data-tooltip-place="bottom"
        data-tooltip-offset={5}
      >
        {labelContent}
      </div>
    </div>
  );
};

export type {
  DropdownOptionType,
  ComboBoxOptionType,
  DiscreteOption,
  TooltipProps,
};

export {
  ClickableIcon,
  ComboBox,
  DropdownInput,
  DiscreteInput,
  ToggleInput,
  AutoLayoutSelector,
  ColorInput,
  EmptyInputState,
  useComboboxContainer,
  ComboboxContainer,
};
